import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { verifyPayment } from '../utils/thawani';
import { useOrders } from '../hooks/useOrders';
import { supabase } from '../config/supabase';
import { orderConfirmationPath } from '../utils/orderReference';
import styled from 'styled-components';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  text-align: center;
  min-height: 80vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const SuccessIcon = styled.div`
  font-size: 64px;
  color: #10b981;
  margin-bottom: 1.5rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1rem;
`;

const Message = styled.p`
  color: #4b5563;
  margin-bottom: 2rem;
  text-align: center;
  max-width: 600px;
`;

const Button = styled.button`
  background-color: #10b981;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 0.375rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
  border: none;

  &:hover {
    background-color: #059669;
  }
`;

const PAYMENT_SUCCESS_PROGRESS = {
  verifying: 'Verifying your payment…',
  recording: 'Recording your order…',
};

const PaymentSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying');
  const [error, setError] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [progressPhase, setProgressPhase] = useState('verifying');
  const { markOrderPaid } = useOrders();

  useEffect(() => {
    const verifyPaymentStatus = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const sessionId = params.get('session_id');
        
        if (!sessionId) {
          throw new Error('No session ID found');
        }

        // Verify payment with Thawani
        const paymentData = await verifyPayment(sessionId);
        
        if (paymentData.payment_status === 'paid' && paymentData.client_reference_id) {
          const oid = paymentData.client_reference_id;
          await markOrderPaid(oid, sessionId, setProgressPhase);
          const { data: paidRow } = await supabase
            .from('orders')
            .select('id, order_number')
            .eq('id', oid)
            .maybeSingle();
          navigate(orderConfirmationPath(paidRow || { id: oid }), { replace: true });
          return;
        } else {
          throw new Error('Payment not completed');
        }
      } catch (err) {
        console.error('Payment verification failed:', err);
        setError(err.message || 'Failed to verify payment');
        setStatus('error');
      }
    };

    verifyPaymentStatus();
  }, [location.search]);

  const handleBackToHome = () => {
    navigate('/');
  };

  const handleViewOrder = () => {
    if (orderId) {
      navigate(`/orders/${orderId}`);
    } else {
      navigate('/orders');
    }
  };

  if (status === 'verifying') {
    return (
      <Container>
        <div style={{ fontSize: '1.05rem', color: '#374151', maxWidth: '420px' }}>
          {PAYMENT_SUCCESS_PROGRESS[progressPhase] || progressPhase}
        </div>
        <Message style={{ marginTop: '1rem', fontSize: '0.95rem' }}>
          Please keep this page open until we finish.
        </Message>
      </Container>
    );
  }

  if (status === 'error') {
    return (
      <Container>
        <div style={{ color: '#ef4444', fontSize: '4rem', marginBottom: '1rem' }}>⚠️</div>
        <Title>Payment Verification Failed</Title>
        <Message>{error || 'There was an issue verifying your payment.'}</Message>
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
          <Button onClick={handleBackToHome} style={{ backgroundColor: '#6b7280' }}>Back to Home</Button>
          <Button onClick={() => window.location.reload()}>Try Again</Button>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <SuccessIcon>✓</SuccessIcon>
      <Title>Payment Successful!</Title>
      <Message>
        Thank you for your order. Your payment has been received and your order is being processed.
        {orderId && (
          <div style={{ marginTop: '1rem' }}>
            Order ID: <strong>{orderId}</strong>
          </div>
        )}
      </Message>
      <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
        <Button onClick={handleBackToHome}>Back to Home</Button>
        <Button onClick={handleViewOrder} style={{ backgroundColor: '#3b82f6' }}>
          View Order
        </Button>
      </div>
    </Container>
  );
};

export default PaymentSuccess;
